import { useRecurly, UseRecurlyInstance } from "@recurly/react-recurly";
import { TokenPayload } from "@recurly/recurly-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { PWSSessionBackend, ZypeSubscriptionPlan, COAST_MESSAGES } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { axiosErrorReason, AxiosLoaderResponse } from "../../../lib/coastCommon";
import { CoastAlertDisplayError } from "../common/coast-alert/CoastAlertDisplay.component";
import { CoastGiftRecipient, CoastGiftRecipientInputs } from "./CoastGiftRecipient.component";
import { CoastPaymentDetails, PaymentFormInputs } from "./CoastPaymentDetails.component";
import { CoastPickYourPlan, CoastPickYourPlanInputs } from "./CoastPickYourPlan.component";
import { CoastSignUpBillingInfo, CoastSignUpBillingInfoInputs } from "./CoastSignUpBillingInfo.component";
import { CoastSignUpReviewAndSubmit, CoastSignUpReviewAndSubmitInputs } from "./CoastSignUpReviewAndSubmit.component";
import { CoastSuccessfullySubscribed } from "./CoastSuccessfullySubscribed.components";
import { CoastVerifyEmail, EmailFormInputs } from "./CoastVerifyEmail.component";

const log = ILog.logger("CoastSubscribe.component");

function processRecurlyToken(
  recurly: UseRecurlyInstance,
  ref: React.RefObject<HTMLFormElement>,
  isBankAccount: boolean,
) {
  return new Promise<TokenPayload>((resolve, reject) => {
    if (!ref.current) {
      return reject("Error processing ReCurly Payment: missing form ref");
    }
    if (isBankAccount) {
      recurly.bankAccount.token(ref.current, (err, token) => {
        if (err) {
          return reject(err);
        }
        resolve(token);
      });
    } else {
      recurly.token(ref.current, (err, token) => {
        if (err) {
          return reject(err);
        }
        resolve(token);
      });
    }
  });
}

interface CoastSubscribeProps {
  availablePlans: AxiosLoaderResponse<ZypeSubscriptionPlan[]>;
  setIsGift: (v: boolean) => void;
  session: PWSSessionBackend;
  onCancel: () => void;
  isGift: boolean;
  isAccountCreated: boolean;
}

enum SUBSCRIBE_STEPS {
  PICK_PLAN,
  CREATE_ACCOUNT,
  GIFT_RECIPIENT,
  PAYMENT_INFO,
  BILLING_INFO,
  REVIEW_AND_SUBMIT,
  SUCCESS,
}

const subscribeSteps = (isGift: boolean) =>
  isGift
    ? [
        SUBSCRIBE_STEPS.PICK_PLAN,
        SUBSCRIBE_STEPS.CREATE_ACCOUNT,
        SUBSCRIBE_STEPS.GIFT_RECIPIENT,
        SUBSCRIBE_STEPS.PAYMENT_INFO,
        SUBSCRIBE_STEPS.BILLING_INFO,
        SUBSCRIBE_STEPS.REVIEW_AND_SUBMIT,
        SUBSCRIBE_STEPS.SUCCESS,
      ]
    : [
        SUBSCRIBE_STEPS.PICK_PLAN,
        SUBSCRIBE_STEPS.CREATE_ACCOUNT,
        SUBSCRIBE_STEPS.PAYMENT_INFO,
        SUBSCRIBE_STEPS.BILLING_INFO,
        SUBSCRIBE_STEPS.REVIEW_AND_SUBMIT,
        SUBSCRIBE_STEPS.SUCCESS,
      ];

export const CoastSubscribe = ({
  onCancel,
  isGift,
  setIsGift,
  availablePlans,
  session,
  isAccountCreated,
}: CoastSubscribeProps) => {
  const steps = useMemo(() => subscribeSteps(isGift), [isGift]);
  const [isProcessing, setIsProcessing] = useState(false);
  const paymentFormRef = useRef<HTMLFormElement>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formError, setFormError] = useState("");
  const recurly = useRecurly();

  const paymentDetailsFormMethods = useForm<PaymentFormInputs>({
    defaultValues: { paymentType: "card", accountType: "checking" },
  });
  const reviewAndSubmitFormMethods = useForm<CoastSignUpReviewAndSubmitInputs>();
  const giftRecipientFormMethods = useForm<CoastGiftRecipientInputs>();
  const pickYourPlanFormMethods = useForm<CoastPickYourPlanInputs>();
  const billingInfoFormMethods = useForm<CoastSignUpBillingInfoInputs>();
  const billingInfoValues = billingInfoFormMethods.getValues();
  const emailFormMethods = useForm<EmailFormInputs>();

  useEffect(() => {
    if (availablePlans.error) {
      setFormError(availablePlans.error);
    }
  }, [availablePlans.error]);

  const goForward = useCallback(() => setCurrentStep(currentStep + 1), [currentStep]);
  const goBack = useCallback(() => setCurrentStep(currentStep - 1), [currentStep]);
  const goBacktoStepOne = useCallback(() => setCurrentStep(currentStep - 2), [currentStep]);

  const purchaseSubscription = useCallback(
    async (accessToken: string, recurlyToken: string) => {
      const resp = await session.memberService.subscribe(accessToken, {
        subscription: {
          plan_id: pickYourPlanFormMethods.getValues("planId"),
          recurly_token_id: recurlyToken,
        },
        user: {
          name: `${billingInfoValues.firstName} ${billingInfoValues.lastName}`,
          terms: true,
        },
      });

      return resp.subscription.consumer;
    },
    [billingInfoValues.firstName, billingInfoValues.lastName, pickYourPlanFormMethods, session.memberService],
  );

  const presentGiftSubscription = useCallback(
    async (accessToken: string, recurlyToken: string) => {
      if (!session.giftRecipient?.userId) {
        return null;
      }

      const { donor } = await session.memberService.presentGiftSubscription(accessToken, {
        subscription: {
          plan_id: pickYourPlanFormMethods.getValues("planId"),
          recurly_token_id: recurlyToken,
        },
        donor: {
          name: `${billingInfoValues.firstName} ${billingInfoValues.lastName}`,
        },
        recipient: {
          id: session.giftRecipient.userId,
          name: giftRecipientFormMethods.getValues("name"),
        },
      });

      return donor;
    },
    [
      billingInfoValues.firstName,
      billingInfoValues.lastName,
      giftRecipientFormMethods,
      pickYourPlanFormMethods,
      session.giftRecipient?.userId,
      session.memberService,
    ],
  );

  const processSubscribe = async () => {
    const { accessToken } = session.state;

    if (!accessToken) {
      log.error("Error processing subscription: missing accessToken");
      setFormError(COAST_MESSAGES.MISSING_ACCESS_TOKEN);
      return;
    }

    setIsProcessing(true);
    if (formError) {
      setFormError("");
    }

    let recurlyToken: string;

    try {
      recurlyToken = (
        await processRecurlyToken(
          recurly,
          paymentFormRef,
          paymentDetailsFormMethods.getValues("paymentType") === "bank-account",
        )
      ).id;
    } catch (e) {
      console.error(e); // log.error is printing [object object]
      setFormError(e.message || COAST_MESSAGES.REQUEST_PROCESSING_ERROR);
      return setIsProcessing(false);
    }

    try {
      const profile = isGift
        ? await presentGiftSubscription(accessToken, recurlyToken)
        : await purchaseSubscription(accessToken, recurlyToken);

      session.sessionUpdated({ profile });
      goForward();
    } catch (e) {
      log.error(e);
      setFormError(axiosErrorReason(e) || COAST_MESSAGES.REQUEST_PROCESSING_ERROR);
      setIsProcessing(false);
    }
  };

  const stepProps = useMemo(
    () => ({ stepCount: steps.length, stepNumber: currentStep + 1 }),
    [currentStep, steps.length],
  );

  if (!availablePlans.data) {
    return (
      <main className="component-coast-subscribe">
        <CoastAlertDisplayError message={formError || availablePlans.error} isDisplayed={!!formError} />
      </main>
    );
  }
  if (isAccountCreated && steps[currentStep] === SUBSCRIBE_STEPS.CREATE_ACCOUNT) {
    setCurrentStep(2);
  }
  return (
    <main className="component-coast-subscribe">
      {/* Hide things instead of conditionally rendering them to keep form state from re-setting */}
      <FormProvider {...pickYourPlanFormMethods}>
        <CoastPickYourPlan
          hidden={steps[currentStep] !== SUBSCRIBE_STEPS.PICK_PLAN}
          plans={availablePlans.data}
          setIsGift={setIsGift}
          goForward={goForward}
          onCancel={onCancel}
          isGift={isGift}
          showCountrySelect={isAccountCreated}
          {...stepProps}
        />
      </FormProvider>

      <FormProvider {...emailFormMethods}>
        <CoastVerifyEmail
          hidden={steps[currentStep] !== SUBSCRIBE_STEPS.CREATE_ACCOUNT}
          goForward={goForward}
          onCancel={onCancel}
          session={session}
          goBack={goBack}
          {...stepProps}
        />
      </FormProvider>

      <FormProvider {...giftRecipientFormMethods}>
        <CoastGiftRecipient
          hidden={steps[currentStep] !== SUBSCRIBE_STEPS.GIFT_RECIPIENT}
          goForward={goForward}
          onCancel={onCancel}
          session={session}
          goBack={goBack}
          stepNumber={isAccountCreated ? currentStep : currentStep + 1}
          stepCount={steps.length}
        />
      </FormProvider>

      <FormProvider {...paymentDetailsFormMethods}>
        <CoastPaymentDetails
          hidden={steps[currentStep] !== SUBSCRIBE_STEPS.PAYMENT_INFO}
          country={pickYourPlanFormMethods.getValues("country")}
          firstName={billingInfoValues.firstName}
          lastName={billingInfoValues.lastName}
          address1={billingInfoValues.address1}
          address2={billingInfoValues.address2}
          city={billingInfoValues.city}
          state={billingInfoValues.state}
          zip={billingInfoValues.zip}
          phone={billingInfoValues.phone}
          goForward={goForward}
          ref={paymentFormRef}
          onCancel={onCancel}
          goBacktoStepOne={goBacktoStepOne}
          {...stepProps}
          stepNumber={isAccountCreated ? currentStep : currentStep + 1}
          stepCount={steps.length}
        />
      </FormProvider>

      <FormProvider {...billingInfoFormMethods}>
        <CoastSignUpBillingInfo
          hidden={steps[currentStep] !== SUBSCRIBE_STEPS.BILLING_INFO}
          country={pickYourPlanFormMethods.getValues("country")}
          goForward={goForward}
          onCancel={onCancel}
          goBack={goBack}
          {...stepProps}
        />
      </FormProvider>

      <FormProvider {...reviewAndSubmitFormMethods}>
        <CoastSignUpReviewAndSubmit
          hidden={steps[currentStep] !== SUBSCRIBE_STEPS.REVIEW_AND_SUBMIT}
          planId={pickYourPlanFormMethods.getValues("planId")}
          subscribe={processSubscribe}
          isProcessing={isProcessing}
          plans={availablePlans.data}
          onCancel={onCancel}
          isGift={isGift}
          country={pickYourPlanFormMethods.getValues("country")}
          session={session}
          {...stepProps}
          goBack={goBack}
        />
      </FormProvider>

      {/* no forms here - can do conditional render */}
      {steps[currentStep] === SUBSCRIBE_STEPS.SUCCESS ? <CoastSuccessfullySubscribed isGift={isGift} /> : null}

      <CoastAlertDisplayError message={formError} isDisplayed={!!formError} />
    </main>
  );
};
